import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, catchError, map } from 'rxjs';

import { oofApi as oofAdminApi, oofDtos as oofAdminDtos } from '@vectronic/openApi-admin';
import { oofApi, oofDtos } from '@vectronic/openApi-customer';
import { OrdersFilter } from '../dto/orders-filter.dto';
import {
  CreateOrderItemConfigurationDto,
  EditOrderItemConfigurationDto,
} from '../models/OrderItemConfiguration.model';

@Injectable({ providedIn: 'root' })
export class OrdersRestService {
  adminApi = inject(oofAdminApi.BffAdminOofWeb);
  customerApi = inject(oofApi.BffOofWeb);

  getOrdersByFilter(filter: OrdersFilter, page = 0, size = 25): Observable<oofDtos.PageOrder> {
    return this.customerApi.getOrdersByFilter(filter, page, size).pipe(catchError(() => EMPTY));
  }
  getOrdersByMultiSelectFilter(
    filter: oofAdminDtos.OrderFilterRequest,
    page = 0,
    size = 25,
  ): Observable<oofAdminDtos.PageOofOrder> {
    return this.adminApi
      .getOrdersByMultiSelectFilter(filter, page, size)
      .pipe(catchError(() => EMPTY));
  }

  getOrderById(id: string): Observable<oofDtos.Order> {
    return this.customerApi.getOrderById(id);
  }

  getOrderHistory(orderId: string): Observable<oofAdminDtos.OrderHistoryItem[]> {
    return this.adminApi.getOrderHistory(orderId);
  }

  updateOrder(
    orderId: string,
    order: oofDtos.Order,
    sendNotification = true,
  ): Observable<oofDtos.Order> {
    return this.customerApi.updateOrder(orderId, order, sendNotification);
  }

  addOrderComment(orderId: string, comment: string): Observable<oofDtos.Order> {
    return this.adminApi
      .addOrderComment(orderId, { comment })
      .pipe(map((order) => order as oofDtos.Order));
  }

  deleteOrderComment(orderId: string, commentId: string): Observable<oofDtos.Order> {
    return this.adminApi
      .deleteOrderComment(orderId, commentId)
      .pipe(map((order) => order as oofDtos.Order));
  }

  editOrderComment(orderId: string, commentId: string, comment: string): Observable<oofDtos.Order> {
    return this.adminApi
      .editOrderComment(orderId, commentId, { comment })
      .pipe(map((order) => order as oofDtos.Order));
  }

  updateOrderItem(
    orderId: string,
    orderItemId: number,
    orderItem: oofDtos.OrderItem,
  ): Observable<oofDtos.OrderItem> {
    return this.customerApi
      .updateOrderItemByPosition(orderId, orderItemId, orderItem)
      .pipe(catchError(() => EMPTY));
  }

  createOrderItemConfiguration(
    orderId: string,
    orderItemId: number,
    orderItemConfiguration: CreateOrderItemConfigurationDto,
  ): Observable<oofDtos.OrderItemConfigurationModificationResult> {
    return this.customerApi.createOrderItemConfiguration(
      orderId,
      orderItemId,
      orderItemConfiguration,
    );
  }

  updateOrderItemConfiguration(
    orderId: string,
    orderItemId: number,
    orderItemConfigurationId: string,
    orderItemConfiguration: EditOrderItemConfigurationDto,
    sendNotification = true,
  ): Observable<oofDtos.OrderItemConfigurationModificationResult> {
    return this.customerApi.updateOrderItemConfiguration(
      orderItemConfiguration.version,
      orderId,
      orderItemId,
      orderItemConfigurationId,
      orderItemConfiguration,
      sendNotification,
    );
  }

  updateOrderItemConfigurationFormData(
    version: number,
    orderId: string,
    orderItemId: number,
    orderConfigurationId: string,
    formDataDto: oofDtos.FormData,
  ) {
    return this.customerApi.updateOrderItemConfigurationFormData(
      version,
      orderId,
      orderItemId,
      orderConfigurationId,
      formDataDto,
    );
  }

  deleteOrderItemConfiguration(
    orderId: string,
    orderItemId: number,
    orderItemConfigurationId: string,
  ): Observable<oofDtos.OrderItemConfigurationModificationResult> {
    return this.customerApi.deleteOrderItemConfiguration(
      orderId,
      orderItemId,
      orderItemConfigurationId,
    );
  }

  applyToAll(
    orderId: string,
    orderItemId: number,
    body: { versions: Record<string, number>; update: Record<string, unknown> },
  ) {
    return this.adminApi.applyToAll(orderId, orderItemId, body);
  }

  toFilterValues(values: string[] | undefined): string[] | undefined {
    const filterValues =
      values
        ?.flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter(Boolean) ?? [];

    return filterValues.length ? filterValues : undefined;
  }
}
